import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';

import { SearchObjectResult } from '../../../shared/search-object-result.model';
import { LocalLoad } from '../../shared/local-load.model';
import { LocalLoadSearchCriteria } from '../../shared/local-load-search-criteria.model';
import { Observable, of, Subscription } from 'rxjs';
import * as moment from 'moment';
import { LOCAL_STORAGE_KEYS } from '../../../core/constants';
import { AuthenticationService } from '../../../shared/services/authentication.service';
import { LoaderService } from '../../../shared/services/loader.service';
import { TranslateService } from '@ngx-translate/core';
import { ModalService } from '../../../shared/services/modal.service';
import { ToastrService } from 'ngx-toastr';
import { LocalLoadService } from '../../shared/local-load.service';
import { CarrierService } from '../../../user-dashboard/shared/carrier.service';
import { DataFiltersComponent } from '../../../shared/data-filters/data-filters.component';
import { ReorderLoadsService } from '../../shared/reorder-loads.service';
import { finalize } from 'rxjs/operators';
import { Option } from '../../../shared/data-filters/data-filter-autocomplete/data-filter-autocomplete.component';

@Component({
  selector: 'app-vehicle-loads-list',
  templateUrl: './vehicle-loads-list.component.html',
  styleUrls: ['./vehicle-loads-list.component.scss']
})
export class VehicleLoadsListComponent implements OnInit, OnDestroy, OnChanges {

  @ViewChild('dataFilters', { static : true })
  dataFilters: DataFiltersComponent;

  MAX_PAGE_SIZE = 2147483647;

  draggableListName = 'vehicleList';

  carrierSelectionAvailable = false;

  @Input()
  selectedCarrierId: string;

  @Input()
  canAssignVehicle = true;

  filtersBase: LocalLoadSearchCriteria = {
    status: []
  };

  searchResult: SearchObjectResult<LocalLoad>;

  filters: LocalLoadSearchCriteria = { ...this.filtersBase };

  lastSearchFilters: LocalLoadSearchCriteria = { ...this.filters };

  completedStartDate: moment.Moment;
  completedEndDate: moment.Moment;

  loadPageForVehicle: (page: number, pageSize: number) => Observable<SearchObjectResult<LocalLoad>> = null;
  searchVehicles: () => Observable<any>                                                             = null;
  reorderLoad: (vehicle: string, localLoadIds: Array<string>, position: number) => Observable<any>  = null;
  reorderLoadTop: (vehicle: string, localLoadIds: Array<string>) => Observable<any>                 = null;
  reorderLoadBottom: (vehicle: string, localLoadIds: Array<string>) => Observable<any>              = null;
  unassignLoadsFromVehicle: (loadUuids: string[]) => Observable<any>                                = null;

  vehicleSearchOptions: Option[] = [];
  subscription: Subscription;

  constructor(
    private authService: AuthenticationService,
    private loader: LoaderService,
    private translate: TranslateService,
    private modalService: ModalService,
    private toastr: ToastrService,
    private localLoadsService: LocalLoadService,
    private carrierService: CarrierService,
    private reorderService: ReorderLoadsService,
  ) {
    const localStorageFilters: LocalLoadSearchCriteria = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.REORDER_LOADS_VEHICLE));

    if (localStorageFilters) {
      this.applyFilters(localStorageFilters);
    }

    if (this.authService.canManageCarriers()) {
      this.carrierSelectionAvailable = true;
      this.selectedCarrierId         = localStorage.getItem(LOCAL_STORAGE_KEYS.REORDER_LOADS_CARRIER);
      this.loadPageForVehicle        = (page: number, pageSize: number) => {
        if (this.filters.vehicles != null) {
          this.reorderService.chosenVehicle(this.filters.vehicles.toString());
          const reorderLoadsFilters = { ...this.filters };
          if (reorderLoadsFilters.showCompleted) {
            reorderLoadsFilters.status                  = ['PLANNED', 'ON_GOING', 'COMPLETED'];
            this.completedStartDate                     = moment().subtract(7, 'days');
            this.completedEndDate                       = moment();
            reorderLoadsFilters['completed_start_date'] = this.completedStartDate.utc(true).valueOf();
            reorderLoadsFilters['completed_end_date']   = this.completedEndDate.utc(true).valueOf();
          } else {
            reorderLoadsFilters.status = ['PLANNED', 'ON_GOING'];
          }
          return this.localLoadsService.searchForReorderCarrier(page - 1, this.MAX_PAGE_SIZE, this.selectedCarrierId, reorderLoadsFilters);
        } else {
          this.reorderService.chosenVehicle(null);
          return of({ results: [], count: 0, totalCount: 0, page: 0, totalPages: 0, pageSize: 0 })
        }
      };
      this.unassignLoadsFromVehicle  = (loadUuids) =>
        this.localLoadsService.unassignLoadsFromVehicleForCarrier(this.selectedCarrierId, loadUuids);
      this.searchVehicles            = () => this.localLoadsService.getVehiclesForCarrier(this.selectedCarrierId);
      this.reorderLoad               = (vehicle, localLoadIds, position) =>
        this.localLoadsService.reorderLoadForCarrier(this.selectedCarrierId, vehicle, localLoadIds, position);
      this.reorderLoadTop            = (vehicle, localLoadIds) =>
        this.localLoadsService.orderLoadToTopForCarrier(this.selectedCarrierId, vehicle, localLoadIds);
      this.reorderLoadBottom         = (vehicle, localLoadIds) =>
        this.localLoadsService.orderLoadToBottomForCarrier(this.selectedCarrierId, vehicle, localLoadIds)
    } else {
      this.loadPageForVehicle       = (page: number, pageSize: number) => {
        if (this.filters.vehicles != null) {
          this.reorderService.chosenVehicle(this.filters.vehicles.toString());
          const reorderLoadsFilters = { ...this.filters };
          if (reorderLoadsFilters.showCompleted) {
            reorderLoadsFilters.status                  = ['PLANNED', 'ON_GOING', 'COMPLETED'];
            this.completedStartDate                     = moment().subtract(7, 'days');
            this.completedEndDate                       = moment();
            reorderLoadsFilters['completed_start_date'] = this.completedStartDate.utc(true).valueOf();
            reorderLoadsFilters['completed_end_date']   = this.completedEndDate.utc(true).valueOf();
          } else {
            reorderLoadsFilters.status = ['PLANNED', 'ON_GOING'];
          }
          return this.localLoadsService.searchReorderForSelfCarrier(page - 1, this.MAX_PAGE_SIZE, reorderLoadsFilters);
        } else {
          this.reorderService.chosenVehicle(null);
          return of(
            { results: [], count: 0, totalCount: 0, page: 0, totalPages: 0, pageSize: 0 })
        }
      };
      this.unassignLoadsFromVehicle = (loadUuids) =>
        this.localLoadsService.unassignLoadsFromVehicleForSelfCarrier(loadUuids);
      this.searchVehicles           = () => this.localLoadsService.getVehiclesForSelfCarrier();
      this.reorderLoad              = (vehicle, localLoadIds, position) =>
        this.localLoadsService.reorderLoadForSelfCarrier(vehicle, localLoadIds, position);
      this.reorderLoadTop           = (vehicle, localLoadIds) =>
        this.localLoadsService.orderLoadForToTopSelfCarrier(vehicle, localLoadIds);
      this.reorderLoadBottom        = (vehicle, localLoadIds) =>
        this.localLoadsService.orderLoadToBottomForSelfCarrier(vehicle, localLoadIds);
    }
  }

  ngOnInit() {
    this.subscription = this.reorderService.assignEvent.subscribe(() => {
      this.dataFilters.apply();
    });
  }

  applyFilters(filters: LocalLoadSearchCriteria) {
    if (!filters.status.length) {
      filters.status = ['PLANNED', 'ON_GOING'];
    }
    this.filters           = filters;
    this.lastSearchFilters = { ...this.filters };
  }

  pageChanged() {
    this.lastSearchFilters = { ...this.filters };

    localStorage.setItem(LOCAL_STORAGE_KEYS.REORDER_LOADS_VEHICLE, JSON.stringify(this.lastSearchFilters));

    if (this.carrierSelectionAvailable) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.REORDER_LOADS_CARRIER, this.selectedCarrierId);
    }
  }

  onLoad(searchResult: SearchObjectResult<LocalLoad>) {
    this.searchResult = searchResult;

    this.loader.startLoad();
    this.searchVehicles()
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(vehicles => {
        this.vehicleSearchOptions = vehicles.map(vehicle => ({ value: vehicle, label: vehicle }));
      });
  }

  beforeReset() {
    this.completedStartDate = null;
    this.completedEndDate   = null;
  }

  refreshLoads() {
    this.dataFilters.apply();
    this.reorderService.unassignLoad();
  }

  unassignLoad(load: LocalLoad) {
    event.stopPropagation();
    this.unassignLoadsFromVehicle([load.loadUuid])
      .subscribe(() => {
        this.refreshLoads();
        this.toastr.success(this.translate.instant('local-loads.update.success.unassign-vehicle'));
      }, err => {
        console.log(err);
        this.toastr.error(this.translate.instant('local-loads.update.errors.unassign-vehicle'));
      });
  }

  unassignAllLoads(content, loads: LocalLoad[]) {
    event.stopPropagation();
    this.modalService.open({
      title: this.translate.instant('local-loads.reorder.modal.unassign.title'),
      content,
      validateButton: {
        text: this.translate.instant('local-loads.reorder.modal.unassign.confirm')
      },
      closeButton: {
        text: this.translate.instant('local-loads.reorder.modal.unassign.close')
      },
      size: 'sm'
    }).subscribe(() => {
      this.unassignLoadsFromVehicle(loads.map(load => load.loadUuid))
        .subscribe(() => {
          this.refreshLoads();
          this.onLoad(this.searchResult);
          this.toastr.success(this.translate.instant('local-loads.update.success.unassign-vehicle'));
        }, err => {
          console.log(err);
          this.toastr.error(this.translate.instant('local-loads.update.errors.unassign-vehicle'));
        });
    });
  }

  changePosition(reorderLoads: any) {
    if (reorderLoads.index === 0) {
      this.moveVehicleToTop(reorderLoads.item);
    } else {
      const beforeItemPosition = reorderLoads.items[reorderLoads.index - 1].position;
      if (beforeItemPosition) {
        this.reorderLoad(reorderLoads.item.vehicle, [reorderLoads.item.id], beforeItemPosition + 1)
          .pipe(finalize(() => this.loader.stopLoad()))
          .subscribe(() => {
            this.refreshLoads();
            this.toastr.success(this.translate.instant('local-loads.reorder.success.reorder-load'));
          }, () => {
            this.toastr.error(this.translate.instant('local-loads.reorder.errors.reorder-load'));
          });
      } else {
        const listOfNullLoads    = [];
        let firstNonNullPosition = null;
        for (let i = reorderLoads.index - 1; i >= 0; i--) {
          if (reorderLoads.items[i].position) {
            firstNonNullPosition = reorderLoads.items[i].position;
            break;
          } else {
            listOfNullLoads.push(reorderLoads.items[i].id);
          }
        }
        if (firstNonNullPosition) {
          this.reorderLoad(reorderLoads.item.vehicle, [...listOfNullLoads, reorderLoads.item.id], firstNonNullPosition + 1)
            .pipe(finalize(() => this.loader.stopLoad()))
            .subscribe(() => {
              this.refreshLoads();
              this.toastr.success(this.translate.instant('local-loads.reorder.success.reorder-load'));
            }, () => {
              this.toastr.error(this.translate.instant('local-loads.reorder.errors.reorder-load'));
            });
        } else {
          this.reorderLoad(reorderLoads.item.vehicle, [...listOfNullLoads, reorderLoads.item.id], 1)
            .pipe(finalize(() => this.loader.stopLoad()))
            .subscribe(() => {
              this.refreshLoads();
              this.toastr.success(this.translate.instant('local-loads.reorder.success.reorder-load'));
            }, () => {
              this.toastr.error(this.translate.instant('local-loads.reorder.errors.reorder-load'));
            });
        }
      }
    }
  }

  moveVehicleToTop(load: any) {
    event.stopPropagation();
    this.loader.startLoad();
    this.reorderLoadTop(load.vehicle, [load.id])
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(() => {
        this.refreshLoads();
        this.toastr.success(this.translate.instant('local-loads.reorder.success.reorder-load'));
      }, () => {
        this.toastr.error(this.translate.instant('local-loads.reorder.errors.reorder-load'));
      });
  }

  moveVehicleToBottom(load: any, loads: any) {
    event.stopPropagation();
    const loadsBefore = loads
      .filter(loadBefore => loadBefore.position === null && loadBefore.id !== load.id)
      .map(loadBefore => loadBefore.id);
    this.loader.startLoad();
    this.reorderLoadBottom(load.vehicle, [...loadsBefore, load.id])
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(() => {
        this.refreshLoads();
        this.toastr.success(this.translate.instant('local-loads.reorder.success.reorder-load'));
      }, () => {
        this.toastr.error(this.translate.instant('local-loads.reorder.errors.reorder-load'));
      });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.dataFilters.reset();
    this.dataFilters.apply();
  }

}
